import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ShieldCheck, Leaf, Clock, Heart, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/918882183014?text=Hi%2C%20I%20want%20to%20book%20residential%20water%20tank%20cleaning%20in%20Gurugram";

const faqs = [
  { q: "Is the cleaning safe for my family and pets?", a: "100% safe. We use only food-grade, WHO-approved cleaning agents that are non-toxic to humans, pets, and plants. Your tank is ready for immediate water storage after cleaning — no waiting period required." },
  { q: "How long does residential tank cleaning take?", a: "A standard home tank (500-2000 litres) takes 2-3 hours. We arrive on time, complete the work efficiently, and leave your premises clean. You'll have fresh water within hours." },
  { q: "Do I need to be home during the cleaning?", a: "We recommend having someone present during the initial inspection and final handover. However, if you provide tank access, our team can complete the work independently and share before/after photos via WhatsApp." },
  { q: "How do I prepare my tank for cleaning?", a: "Simply reduce water usage 24 hours before the appointment so the tank level is low. No other preparation needed — our team handles everything including draining, cleaning, and refilling." },
  { q: "What is included in the ₹999 residential package?", a: "The ₹999 package includes: complete draining, manual scrubbing, high-pressure jet cleaning, anti-bacterial disinfection, and a 30-day hygiene guarantee. Applicable for tanks up to 1,000 litres." },
];

const steps = [
  { title: "Pre-Cleaning Inspection", desc: "Visual inspection of your tank, water quality check, and assessment of cleaning requirements." },
  { title: "Safe Draining", desc: "Careful draining of existing water. If usable, we can redirect it for garden/cleaning use to avoid wastage." },
  { title: "Deep Cleaning", desc: "Manual scrubbing of stubborn deposits followed by high-pressure jet cleaning of all surfaces." },
  { title: "Sanitization & Handover", desc: "Food-grade anti-bacterial treatment, UV sanitization, and tank ready for immediate water filling." },
];

function FAQ({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors">
        <span className="font-medium text-navy text-sm pr-4">{faq.q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-sky flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-sky flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed">{faq.a}</div>}
    </div>
  );
}

export default function ResidentialTankCleaning() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Residential Water Tank Cleaning in Gurugram | TankBuddy</title>
        <meta name="description" content="Residential water tank cleaning in Gurugram for homes, apartments, and villas. Family-safe eco-friendly products. Starting ₹999. Same-day service. Book TankBuddy!" />
        <meta name="keywords" content="residential tank cleaning Gurugram, home water tank cleaning Gurgaon, apartment tank cleaning, villa tank cleaning" />
        <link rel="canonical" href="https://www.tankbuddy.in/residential-tank-cleaning-gurugram" />
        <meta property="og:title" content="Residential Water Tank Cleaning in Gurugram | TankBuddy" />
        <meta property="og:description" content="Residential water tank cleaning in Gurugram. Family-safe, eco-friendly. Starting ₹999. Same-day service." />
        <meta property="og:url" content="https://www.tankbuddy.in/residential-tank-cleaning-gurugram" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Residential Water Tank Cleaning in Gurugram",
            "provider": {
              "@type": "LocalBusiness",
              "name": "TankBuddy",
              "telephone": "+91-8882183014",
              "url": "https://www.tankbuddy.in",
              "address": {"@type": "PostalAddress", "addressLocality": "Gurugram", "addressRegion": "Haryana", "addressCountry": "IN"}
            },
            "areaServed": "Gurugram",
            "description": "Professional residential water tank cleaning for homes, apartments, and villas in Gurugram. Family-safe eco-friendly products.",
            "offers": {"@type": "Offer", "price": "999", "priceCurrency": "INR", "priceValidUntil": "2026-12-31"}
          }
        `}</script>
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Residential Water Tank Cleaning in Gurugram
          </h1>

          <div className="flex flex-wrap gap-4 mb-10">
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><ShieldCheck className="w-4 h-4" /> Certified Technicians</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Leaf className="w-4 h-4" /> Eco-friendly</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Clock className="w-4 h-4" /> Same-day Service</span>
            <span className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 text-sm font-medium px-3 py-1.5 rounded-full"><Heart className="w-4 h-4" /> Family-safe Products</span>
          </div>

          <div className="prose prose-slate max-w-none mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Clean Water for Your Home & Family</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Your family's health starts with clean water. TankBuddy's residential water tank cleaning service is specifically designed for homes, apartments, villas, and independent houses in Gurugram. We understand that families need a quick, thorough, and safe cleaning process that doesn't disrupt daily routines or leave behind any harmful chemical residue.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our residential service uses exclusively food-grade, WHO-approved cleaning agents that are 100% safe for drinking water, safe for children, safe for pets, and safe for plants. Every product we use is biodegradable and eco-friendly. After our cleaning, your tank is immediately ready for water storage — no waiting period, no chemical smell, no compromise on your family's health.
            </p>

            <h2 className="font-heading font-bold text-2xl text-navy mb-4 mt-10">For Every Type of Home in Gurugram</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Whether you live in a DLF villa with a 5,000-litre overhead tank, a 2BHK apartment in Sushant Lok with a shared society tank, or an independent house in Palam Vihar with both overhead and underground storage — TankBuddy has the right solution. We clean syntax tanks (PVC/plastic), concrete/RCC tanks, steel tanks, and FRP (fiberglass) tanks of all sizes.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our team arrives on time, completes the job within 2-3 hours, cleans up after themselves, and shares before/after photos on WhatsApp so you can see the difference. We recommend cleaning every 6 months for optimal family health — and we'll even send you a friendly reminder when it's time!
            </p>
          </div>

          <div className="bg-surface border border-slate-200 rounded-xl p-8 mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Our Home-Friendly Cleaning Process</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sky font-bold text-sm">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-navy mb-1">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-2xl mb-2">Pricing</h2>
            <p className="text-slate-300 mb-4">Residential tank cleaning in Gurugram</p>
            <div className="text-4xl font-heading font-extrabold mb-1">Starting ₹999</div>
            <p className="text-slate-400 text-sm mb-6">For tanks up to 1,000 litres. Includes jet cleaning, sanitization, and 30-day hygiene guarantee.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+918882183014"><Phone className="w-4 h-4 mr-2" /> Call +91 88821 83014</a>
              </Button>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FAQ key={i} faq={faq} />)}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
